import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import React from 'react';

import { ImageCaption } from '../ImageCaption';
import { asset } from './RichTextAsset.module.scss';
import { video } from './RichTextVideo.module.scss';

const RichTextAsset: React.FC<{
  node: { data: { target: Queries.RichTextAssetFragment } };
}> = ({ node }) => {
  const entry = get(node, 'data.target');
  const isSvg = get(entry, 'file.url', '').endsWith('.svg');
  const isMp4 = get(entry, 'file.url', '').endsWith('.mp4');

  if (!entry) {
    console.warn('[RichTextAsset] Entry not rendered', { node });
    return null;
  }

  const hasCaption = get(entry, 'metadata.tags', []).some(
    (tag) =>
      tag.contentful_id === 'hasCaption' || tag.contentful_id === 'needsCredit',
  );

  return (
    <>
      {isObject(entry.gatsbyImageData) ? (
        <figure className={asset}>
          <GatsbyImage
            image={entry.gatsbyImageData}
            alt={entry.description || entry.title}
          />
          {hasCaption && isString(entry.description) ? (
            <ImageCaption caption={entry.description} />
          ) : null}
        </figure>
      ) : null}

      {isSvg ? (
        <img
          src={entry.file.url}
          alt={entry.description || entry.title}
          width={entry.file.details?.image?.width}
          height={entry.file.details?.image?.height}
          // center this image vertically, it's parent is a div with display: grid
          style={{
            placeSelf: 'center',
            gridColumn: '1 / -1',
          }}
        />
      ) : null}

      {isMp4 ? (
        <video className={video} playsInline autoPlay muted loop>
          <source src={entry.file.url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      ) : null}
    </>
  );
};

export default RichTextAsset;

export const query = graphql`
  fragment RichTextAsset on ContentfulAsset {
    __typename
    contentful_id
    gatsbyImageData(
      formats: [AUTO, AVIF, WEBP]
      quality: 80
      layout: FULL_WIDTH
      placeholder: BLURRED
    )
    description
    title
    file {
      url
      details {
        image {
          width
          height
        }
      }
    }
    metadata {
      tags {
        contentful_id
      }
    }
  }
`;
